import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, filter, map, of, switchMap, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Login } from '../models/Login';
import { Register } from '../models/Register';
import { User } from '../models/User';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private baseUrl = environment.apiBaseUrl;
  private readonly apiKey = '4d6b1b9d7ce8eddd9e81a4a0150c3d34';

  constructor(private http: HttpClient, private router: Router, private userService: UserService) {

  }

  login(user: Login) {
    return this.http.post<User>(`${this.baseUrl}/user/login?_format=json`, user);
  }

  register(userData: Register) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    })

    return this.http.post(`${this.baseUrl}/user/register?_format=json`, userData, { headers });
  }

  registerAddress(userAddress: any) {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/vnd.api+json'
      })
    };
    return this.http.post(`${this.baseUrl}/jsonapi/profile/customer`, userAddress, httpOptions);
  }

  updateAddress(userAddress: any) {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/vnd.api+json'
      })
    };

    return this.http.patch(`${this.baseUrl}/jsonapi/profile/customer/${userAddress.data.id}`, userAddress, httpOptions);
  }

  isAuthenticated(): boolean {
    return !!localStorage.getItem('login');
  }

  getAuthenticatedUserID() {
    const login = localStorage.getItem('login');
    if (login) {
      var json = JSON.parse(login);
      return json.current_user.uid;
    }
    return undefined;
  }


  getCurrentUserUUID() {
    return this.getCurrentUser().pipe(
      filter(response => response.data.length > 0),
      map(response => {
        return response.data[0].id;
      })
    )
  }

  isAdmin(): boolean {
    const authenticated = localStorage.getItem('login');
    if (authenticated) {
      const json = JSON.parse(authenticated);
      if (json.current_user.roles) {
        if (json.current_user.roles.includes("administrator")) {
          return true;
        }
      }
    }
    return false;
  }

  logout(logouttoken: string, csrftoken: string) {
    this.clearAuthStorage();
    const requestBody = {};

    const headers = new HttpHeaders({
      'Content-Type': 'application/json', //x-www-form-urlencoded
      'X-CSRF-Token': csrftoken,
      //'api-key': '4d6b1b9d7ce8eddd9e81a4a0150c3d34'
    });
    // Include the headers in the options object
    const httpOptions = {
      headers: headers,
      //params: {_format: 'json', token: csrftoken, csrf_token: csrftoken}
    };
    return this.http.get(`${this.baseUrl}/api/logout`, httpOptions);
  }

  clearAuthStorage() {
    localStorage.removeItem('login');
    localStorage.removeItem('api-key');
    localStorage.removeItem('userUuid');
    localStorage.removeItem('user_id');
    localStorage.removeItem('display_name');
  }

  checkSession() {
    return this.getCurrentUser().pipe(
      tap((userResponse: any) => {
        if (!userResponse?.data?.length) {
          throw new Error('No authenticated user found');
        }
        this.persistLocalUser(userResponse.data[0]);
      }),
      map(() => true),
      catchError(() => {
        this.clearAuthStorage();
        return of(false);
      })
    );
  }

  checkIsAdmin() {
    return this.getCurrentUser().pipe(
      map((response: any) => {
        const user = response?.data?.[0];
        if (!user?.relationships?.roles?.data) {
          return false;
        }
        return user.relationships.roles.data.some((role: any) =>
          role?.meta?.drupal_internal__target_id === 'administrator'
        );
      }),
      catchError(() => of(false))
    );
  }

  fetchCsrfToken(): Observable<string> {
    return this.http.get<string>(`${this.baseUrl}/session/token`, { responseType: 'text' as 'json' });
  }

  lostPassword(data: any){
    const headers =  new HttpHeaders({
      "Content-Type" : "application/json"
    });
    return this.http.post(`${this.baseUrl}/user/lost-password?_format=json`, data, {headers});
  }

  resetPassword(data: any){
    const headers =  new HttpHeaders({
      "Content-Type" : "application/json"
    });
    return this.http.post(`${this.baseUrl}/user/lost-password-reset?_format=json`, data, {headers});
  }

  updateUser(id: string, body: any){
    const headers = new HttpHeaders({
      "Content-Type" : "application/vnd.api+json"
    });
    return this.http.patch(`${this.baseUrl}/jsonapi/user/user/${id}`,body, {headers});
  }

  updateProfile(id: string, body: any){
    const headers = new HttpHeaders({
      "Content-Type" : "application/vnd.api+json"
    });
    return this.http.patch(`${this.baseUrl}/jsonapi/profile/customer/${id}`, body, {headers});
  }

  getCurrentUser(){
    return this.getCurrentUserUuid().pipe(
      switchMap((uuid: string) => {
        const headers = this.getSessionIdentityHeaders();
        return this.http.get<any>(`${this.baseUrl}/jsonapi/user/user/${uuid}`, { headers }).pipe(
          map((response: any) => {
            if (Array.isArray(response?.data)) {
              return response;
            }
            return {
              ...response,
              data: response?.data ? [response.data] : []
            };
          })
        );
      })
    );
  }

  getCurrentUserUuid() {
    const headers = this.getSessionIdentityHeaders();
    return this.http.get(`${this.baseUrl}/jsonapi`, {headers}).pipe(
      map((response: any) => {
        const id = response?.meta?.links?.me?.meta?.id;
        if (!id) {
          throw new Error('No authenticated session');
        }
        return id;
      }));
    }

    syncLocalAuthState(loginResponse?: any) {
      return this.getCurrentUser().pipe(
        tap((currentUserResponse: any) => {
          const currentUser = currentUserResponse?.data?.[0];
          if (!currentUser) {
            return;
          }
          this.persistLocalUser(currentUser, loginResponse);
        })
      );
    }

    private persistLocalUser(currentUser: any, loginResponse?: any) {
      const storedLogin = localStorage.getItem('login');
      const loginObj = loginResponse ?? (storedLogin ? JSON.parse(storedLogin) : {});
      const roles = (currentUser?.relationships?.roles?.data ?? [])
        .map((role: any) => role?.meta?.drupal_internal__target_id)
        .filter((role: any) => !!role);
      loginObj.current_user = loginObj.current_user ?? {};
      loginObj.current_user.uid = currentUser?.attributes?.drupal_internal__uid ?? loginObj.current_user.uid;
      loginObj.current_user.name = currentUser?.attributes?.name ?? loginObj.current_user.name;
      loginObj.current_user.roles = roles;
      localStorage.setItem('login', JSON.stringify(loginObj));
      localStorage.setItem('userUuid', currentUser.id);
      if (currentUser?.attributes?.display_name) {
        localStorage.setItem('display_name', currentUser.attributes.display_name);
      }
      if (loginResponse?.['api-key']) {
        localStorage.setItem('api-key', JSON.stringify(loginResponse['api-key']));
      }
      this.userService.setLoggedInUser(loginObj);
    }

    private getSessionIdentityHeaders() {
      let headers = new HttpHeaders({
        'Content-Type': 'application/vnd.api+json',
        'X-Skip-Api-Key': 'true'
      });
      const rawApiKey = localStorage.getItem('api-key');
      if (rawApiKey) {
        const apiKey = rawApiKey.replace(/"/g, '');
        if (apiKey) {
          headers = headers.set('api-key', apiKey);
        }
      }
      return headers;
    }

    deleteUser() {
      const headers = new HttpHeaders({
        'Content-Type': 'application/vnd.api+json',
        'api-key' : '4d6b1b9d7ce8eddd9e81a4a0150c3d34'
      });
      return this.getCurrentUserUUID().pipe(
        switchMap(uuid => this.http.delete<any>(`${this.baseUrl}/jsonapi/user/user/${uuid}`, { headers }))
      );
    }
}
